import { queryOptions } from '@tanstack/react-query'

import type { AnnotationBody } from '@/api/annotations'
import { fetchJson, path } from '@/api/client'
import { keys } from '@/api/keys'
import type { Job } from '@/schema'
import type { QueueStats } from '@/stores/queueStore'

/**
 * `/api/jobs*` plus the job *starters* that live on other routers
 * (`/api/index/runs`, `/api/annotate/*`, `/api/media/warm`). Everything here
 * returns the server's own shapes from §5.4–5.7. Story starters live in
 * `api/stories.ts`.
 */

export type JobStatus = Job['status']
export type JobLane = Job['lane']

export interface JobListFilters {
  status?: JobStatus
  type?: string
  lane?: JobLane
  limit?: number
  before_id?: number
}

export interface JobsPage {
  items: Job[]
}

/** `GET /api/jobs/stats` (§5.6). */
export interface JobStats {
  by_lane: Record<JobLane, { queued: number; running: number }>
  oldest_queued_age_s: number | null
  spent_today_usd: number
  paused_until?: string
  paused_for?: 'usage_window' | 'rate_limit'
  interactive_queue_wait_s: number
}

/* ---------------------------------------------------------------- jobs --- */

export function listJobs(filters: JobListFilters = {}, signal?: AbortSignal): Promise<JobsPage> {
  return fetchJson<JobsPage>('/api/jobs', { query: { limit: 50, ...filters }, signal })
}

export function getJob(id: number, signal?: AbortSignal): Promise<Job> {
  return fetchJson<Job>(path`/api/jobs/${id}`, { signal })
}

export function cancelJob(id: number): Promise<Job> {
  return fetchJson<Job>(path`/api/jobs/${id}/cancel`, { method: 'POST' })
}

export function retryJob(id: number, body: { effort?: 'low' | 'medium' | 'high'; model?: 'sonnet' | 'opus' | 'haiku' } = {}): Promise<Job> {
  return fetchJson<Job>(path`/api/jobs/${id}/retry`, { method: 'POST', body })
}

export function getJobStats(signal?: AbortSignal): Promise<JobStats> {
  return fetchJson<JobStats>('/api/jobs/stats', { signal })
}

/** Fold `/api/jobs/stats` into the `queue.stats` SSE shape the store keeps. */
export function statsToQueueStats(s: JobStats, prev?: QueueStats | null): QueueStats {
  const lanes = Object.values(s.by_lane ?? {})
  return {
    queued: lanes.reduce((n, l) => n + (l?.queued ?? 0), 0),
    running: lanes.reduce((n, l) => n + (l?.running ?? 0), 0),
    done: prev?.done ?? 0,
    failed: prev?.failed ?? 0,
    spent_usd: s.spent_today_usd,
    eta_s: prev?.eta_s ?? null,
    paused_until: s.paused_until ?? null,
    paused_for: s.paused_for ?? null,
    interactive_queue_wait_s: s.interactive_queue_wait_s,
    oldest_queued_age_s: s.oldest_queued_age_s ?? undefined,
    by_lane: s.by_lane,
  }
}

export const jobsQuery = (filters: JobListFilters = {}) =>
  queryOptions({
    queryKey: keys.jobs.list(filters),
    queryFn: ({ signal }) => listJobs(filters, signal),
    staleTime: 5_000,
  })

export const jobQuery = (id: number) =>
  queryOptions({
    queryKey: keys.jobs.detail(id),
    queryFn: ({ signal }) => getJob(id, signal),
    staleTime: 2_000,
  })

export const jobStatsQuery = queryOptions({
  queryKey: keys.jobs.stats(),
  queryFn: ({ signal }) => getJobStats(signal),
  staleTime: 10_000,
  refetchInterval: 30_000,
  retry: 1,
})

/* ------------------------------------------------------------ starters --- */

/** `POST /api/index/runs` → `202 {job_id, run_id}` or `409 conflict {existing_job_id}` (§5.4). */
export function startIndex(body: { mode: 'full' | 'incremental'; roots?: string[] }): Promise<{ job_id: number; run_id: number }> {
  return fetchJson('/api/index/runs', { method: 'POST', body })
}

/** `POST /api/annotate/heuristic` → `202 {job_ids}` (§5.3). Default `autopublish: true` (bronze rule). */
export function startHeuristic(body: { uids: string[]; autopublish?: boolean }): Promise<{ job_ids: number[] }> {
  return fetchJson('/api/annotate/heuristic', { method: 'POST', body })
}

export interface AiAnnotateRequest {
  uids: string[]
  provider?: 'claude' | 'gemini' | 'local' | 'openrouter'
  model?: string
  effort?: 'low' | 'medium' | 'high'
  budget_usd?: number
  priority?: 3 | 7
  skip_if_pending?: boolean
}

export interface CostEstimate {
  calls: number
  usd: number | null
  seconds: number
  note?: string
}

/** `POST /api/annotate/ai` → `202` on the `ai_bulk` lane; `503 ai_unavailable`; `409` for uids with an active job. */
export function startAiAnnotate(body: AiAnnotateRequest): Promise<{ parent_job_id: number; job_ids: number[]; estimate: CostEstimate }> {
  return fetchJson('/api/annotate/ai', { method: 'POST', body })
}

/** `POST /api/annotate/estimate` → ledger-based estimate, `usd: null` + `note: 'no data yet'` until ≥ 5 rows. */
export function estimateAiAnnotate(body: Pick<AiAnnotateRequest, 'uids' | 'provider' | 'model' | 'effort'>): Promise<CostEstimate> {
  return fetchJson('/api/annotate/estimate', { method: 'POST', body })
}

/* ------------------------------------------------- deep stage annotation --- */

export type DeepAnnotateMode = 'ai' | 'mock' | 'heuristic_only'
export type AiStatusWord = 'ok' | 'degraded' | 'down' | 'mock' | 'unknown' | 'missing'

export interface DeepAnnotateRequest {
  uid: string
  provider?: 'claude' | 'gemini' | 'local' | 'openrouter'
  model?: string
  effort?: 'low' | 'medium' | 'high'
  /** Total cap for the whole run (all passes), 0.05–20; default 1.50 on the server. */
  budget_usd?: number
  /** How many "look again and fix" rounds; default 1. */
  critique_rounds?: 0 | 1 | 2
  /** The unsaved on-screen document: the run respects its locked (human) features and deletions. */
  context_body?: AnnotationBody
  base_rev?: number
  /** Measure the picture without Claude (free). */
  heuristic_only?: boolean
}

export interface DeepEstimate {
  calls: number
  /** `null` (with `note: 'no data yet'`) until the ledger has enough rows to average. */
  usd: number | null
  cap_usd: number
  seconds: number
  by_pass: { pass: string; purpose: string; usd: number | null; cap_usd: number }[]
  mode: DeepAnnotateMode
  ai_status: AiStatusWord
  /** The AI lanes are paused until then; a run waits in the queue. */
  paused_until?: string
  note?: string
}

export interface DeepFinding {
  code: string
  /** A feature id, `"depth"`, or null. */
  ref: string | null
  severity: 'info' | 'warn' | 'error'
  /** The run fixed it itself. */
  fixed: boolean
  msg: string
}

export interface DeepPass {
  name: string
  status: 'ok' | 'skipped' | 'failed'
  cost_usd: number
  duration_ms: number
  model?: string
  error?: string
  reason?: string
}

/** `job.result` of `ai.annotate_stage_deep`. The job never writes the document: it leaves a pending proposal. */
export interface DeepAnnotateResult {
  provider?: string | null
  model?: string | null
  proposal_id: number
  uid: string
  mode: DeepAnnotateMode
  source: 'ai' | 'mock' | 'heuristic'
  cost_usd: number
  cap_usd: number
  passes: DeepPass[]
  findings: DeepFinding[]
  flags: string[]
  counts: { regions: number; anchors: number; portals: number; paths: number; findings_open: number }
  verdict: 'good' | 'needs_fixes' | 'poor' | null
  score: number | null
  affordances: string[]
  base_rev: number | null
}

/**
 * `POST /api/annotate/deep` → `202 {job_id, mode, estimate}`. `409 {existing_job_id}` while this
 * picture is already being annotated, `413` when the on-screen document is too large to send,
 * `422` for anything that is not a stage.
 */
export function startDeepAnnotate(body: DeepAnnotateRequest): Promise<{ job_id: number; mode: DeepAnnotateMode; estimate: DeepEstimate }> {
  return fetchJson('/api/annotate/deep', { method: 'POST', body })
}

/** `POST /api/annotate/deep/estimate` → calls, cost (or null), time, and whether Claude is there at all. */
export function estimateDeepAnnotate(
  body: Pick<DeepAnnotateRequest, 'uid' | 'provider' | 'model' | 'effort' | 'budget_usd' | 'critique_rounds' | 'heuristic_only'>,
  signal?: AbortSignal,
): Promise<DeepEstimate> {
  return fetchJson('/api/annotate/deep/estimate', { method: 'POST', body, signal })
}

/** `POST /api/media/warm` → `202 {job_id, planned}` (§5.5). */
export function startMediaWarm(body: {
  uids?: string[]
  kinds?: string[]
  variants: ('thumb' | 'preview' | 'poster' | 'raster' | 'char_thumb' | 'sheet@256' | 'sheet@128' | 'audio')[]
}): Promise<{ job_id: number; planned: { count: number; bytes_estimate: number } }> {
  return fetchJson('/api/media/warm', { method: 'POST', body })
}
